import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { configAPI } from '../services/api';
import { setPresentationPrivacyEnabled } from '../utils/presentationPrivacy';

const PresentationPrivacyContext = createContext({ enabled: false, ready: false });
const LAST_KNOWN_MODE_KEY = 'presentationPrivacyLastKnown';

const getLastKnownMode = () => localStorage.getItem(LAST_KNOWN_MODE_KEY) === 'on';

export const usePresentationPrivacy = () => useContext(PresentationPrivacyContext);

export const PresentationPrivacyProvider = ({ children }) => {
  const [state, setState] = useState({ enabled: getLastKnownMode(), ready: false });
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    let active = true;

    const load = async ({ reloadOnChange = false } = {}) => {
      try {
        const response = await configAPI.getPresentationPrivacy();
        const enabled = response.data?.data?.enabled === true;
        if (!active) return;

        if (reloadOnChange && stateRef.current.ready && enabled !== stateRef.current.enabled) {
          setPresentationPrivacyEnabled(enabled);
          window.location.reload();
          return;
        }

        setPresentationPrivacyEnabled(enabled);
        localStorage.setItem(LAST_KNOWN_MODE_KEY, enabled ? 'on' : 'off');
        setState({ enabled, ready: true });
      } catch {
        if (!active) return;
        const enabled = getLastKnownMode();
        setPresentationPrivacyEnabled(enabled);
        setState({ enabled, ready: true });
      }
    };

    load();
    const onFocus = () => load({ reloadOnChange: true });
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return (
    <PresentationPrivacyContext.Provider value={state}>
      {children}
    </PresentationPrivacyContext.Provider>
  );
};

export default PresentationPrivacyContext;
